import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Skeleton } from '../ui/skeleton';
import { 
  Plane, Car, Bus, Truck, Train, Bike, Ship, CircleDot,
  ArrowRight, ArrowLeft, Users, AlertCircle, X, Filter,
  UserPlus, Clock, MapPin
} from 'lucide-react';

// Transport configuration
const TRANSPORT_CONFIG = {
  plane: { icon: Plane, label: { 'pt-BR': 'Avião', 'en-US': 'Airplane' }, color: 'sky' },
  car: { icon: Car, label: { 'pt-BR': 'Carro', 'en-US': 'Car' }, color: 'green' },
  bus: { icon: Bus, label: { 'pt-BR': 'Ônibus', 'en-US': 'Bus' }, color: 'orange' },
  van: { icon: Truck, label: { 'pt-BR': 'Van', 'en-US': 'Van' }, color: 'purple' },
  train: { icon: Train, label: { 'pt-BR': 'Trem', 'en-US': 'Train' }, color: 'red' },
  motorcycle: { icon: Bike, label: { 'pt-BR': 'Moto', 'en-US': 'Motorcycle' }, color: 'yellow' },
  boat: { icon: Ship, label: { 'pt-BR': 'Barco', 'en-US': 'Boat' }, color: 'cyan' },
  other: { icon: CircleDot, label: { 'pt-BR': 'Outro', 'en-US': 'Other' }, color: 'slate' },
};

const COLOR_CLASSES = {
  sky: {
    bg: 'bg-sky-100 dark:bg-sky-900/30',
    text: 'text-sky-700 dark:text-sky-300',
    border: 'border-sky-300 dark:border-sky-700',
    hover: 'hover:bg-sky-200 dark:hover:bg-sky-900/50',
  },
  green: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-300 dark:border-green-700',
    hover: 'hover:bg-green-200 dark:hover:bg-green-900/50',
  },
  orange: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-300',
    border: 'border-orange-300 dark:border-orange-700',
    hover: 'hover:bg-orange-200 dark:hover:bg-orange-900/50',
  },
  purple: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-300',
    border: 'border-purple-300 dark:border-purple-700',
    hover: 'hover:bg-purple-200 dark:hover:bg-purple-900/50',
  },
  red: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-300 dark:border-red-700',
    hover: 'hover:bg-red-200 dark:hover:bg-red-900/50',
  },
  yellow: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-300',
    border: 'border-yellow-300 dark:border-yellow-700',
    hover: 'hover:bg-yellow-200 dark:hover:bg-yellow-900/50',
  },
  cyan: {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    text: 'text-cyan-700 dark:text-cyan-300',
    border: 'border-cyan-300 dark:border-cyan-700',
    hover: 'hover:bg-cyan-200 dark:hover:bg-cyan-900/50',
  },
  slate: {
    bg: 'bg-slate-100 dark:bg-slate-800',
    text: 'text-slate-700 dark:text-slate-300',
    border: 'border-slate-300 dark:border-slate-600',
    hover: 'hover:bg-slate-200 dark:hover:bg-slate-700',
  },
};

// Clickable transport badge for filtering
const FilterableBadge = ({ type, count, direction, isActive, onFilterClick, language }) => {
  const config = TRANSPORT_CONFIG[type] || TRANSPORT_CONFIG.other;
  const Icon = config.icon;
  const colors = COLOR_CLASSES[config.color];

  return (
    <button
      onClick={() => onFilterClick(type, direction)}
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg transition-all cursor-pointer
        ${colors.bg} ${colors.text} ${colors.hover}
        ${isActive ? `ring-2 ring-offset-2 ${colors.border} ring-offset-white dark:ring-offset-slate-900` : ''}
      `}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm font-medium">
        {config.label[language] || config.label['pt-BR']}
      </span>
      <Badge variant="secondary" className="ml-1 text-xs">
        {count}
      </Badge>
    </button>
  );
};

const TransportSummary = ({ tripId }) => {
  const { api } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [membersInfo, setMembersInfo] = useState([]);
  const [stats, setStats] = useState({ outbound: {}, return: {} });
  
  // Filter state
  const [activeFilter, setActiveFilter] = useState(null); // { type: 'plane', direction: 'outbound' }

  useEffect(() => {
    const fetchMembersTransport = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/trips/${tripId}/member-info/all`);
        const data = response.data || [];
        setMembersInfo(data);

        // Calculate statistics
        const outboundStats = {};
        const returnStats = {};
        
        data.forEach(info => {
          const outbound = info.outbound_transport_type || info.transport_type;
          const returnType = info.return_transport_type || info.transport_type;
          
          if (outbound) {
            outboundStats[outbound] = (outboundStats[outbound] || 0) + 1;
          }
          if (returnType) {
            returnStats[returnType] = (returnStats[returnType] || 0) + 1;
          }
        });

        setStats({ outbound: outboundStats, return: returnStats });
      } catch (error) {
        console.error('Error fetching transport info:', error);
      } finally {
        setLoading(false);
      }
    };

    if (tripId) {
      fetchMembersTransport();
    }
  }, [tripId, api]);

  // Filter members based on active filter
  const filteredMembers = useMemo(() => {
    if (!activeFilter) return membersInfo;
    
    return membersInfo.filter(member => {
      const outbound = member.outbound_transport_type || member.transport_type;
      const returnType = member.return_transport_type || member.transport_type;
      
      if (activeFilter.direction === 'outbound') {
        return outbound === activeFilter.type;
      } else {
        return returnType === activeFilter.type;
      }
    });
  }, [membersInfo, activeFilter]);

  // Get members who can offer rides (car with available seats)
  const rideOffers = useMemo(() => {
    return membersInfo.filter(member => {
      const outboundCar = member.outbound_transport_type === 'car' || 
                          (!member.outbound_transport_type && member.transport_type === 'car');
      const returnCar = member.return_transport_type === 'car' || 
                        (!member.return_transport_type && member.transport_type === 'car');
      
      const outboundSeats = member.outbound_car_details?.available_seats || 
                            member.car_details?.available_seats || 0;
      const returnSeats = member.return_car_details?.available_seats || 
                          member.car_details?.available_seats || 0;
      
      return (outboundCar && outboundSeats > 0) || (returnCar && returnSeats > 0);
    });
  }, [membersInfo]);

  const handleFilterClick = (type, direction) => {
    if (activeFilter?.type === type && activeFilter?.direction === direction) {
      setActiveFilter(null);
    } else {
      setActiveFilter({ type, direction });
    }
  };

  const clearFilter = () => setActiveFilter(null);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const hasData = membersInfo.length > 0;
  const membersWithTransport = membersInfo.filter(m => 
    m.outbound_transport_type || m.return_transport_type || m.transport_type
  );

  if (!hasData || membersWithTransport.length === 0) {
    return (
      <Card className="border-slate-200 dark:border-slate-700 border-dashed">
        <CardContent className="py-8 text-center">
          <AlertCircle className="w-10 h-10 text-slate-400 mx-auto mb-2" />
          <p className="text-slate-500 dark:text-slate-400">
            {t('members.noTransportInfo') || 'Nenhum membro informou o transporte ainda'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Ride Offers Alert */}
      {rideOffers.length > 0 && (
        <Card className="border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/10">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                <UserPlus className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-green-700 dark:text-green-300">
                  {t('members.rideOffers') || 'Caronas Disponíveis'}
                </h4>
                <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                  {rideOffers.map(m => m.user_name).join(', ')} {rideOffers.length === 1 ? 'tem' : 'têm'} vagas disponíveis no carro
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statistics Overview with Clickable Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Outbound Stats */}
        <Card className="border-orange-200 dark:border-orange-800/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-orange-600 dark:text-orange-400">
              <ArrowRight className="w-4 h-4" />
              {t('myTrip.transport.outbound') || 'Ida'}
              <span className="text-xs font-normal text-slate-500 ml-auto">
                {t('members.clickToFilter') || 'Clique para filtrar'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.outbound).length > 0 ? (
                Object.entries(stats.outbound).map(([type, count]) => (
                  <FilterableBadge 
                    key={`out-${type}`} 
                    type={type} 
                    count={count} 
                    direction="outbound"
                    isActive={activeFilter?.type === type && activeFilter?.direction === 'outbound'}
                    onFilterClick={handleFilterClick}
                    language={language}
                  />
                ))
              ) : (
                <p className="text-sm text-slate-500">-</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Return Stats */}
        <Card className="border-blue-200 dark:border-blue-800/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-blue-600 dark:text-blue-400">
              <ArrowLeft className="w-4 h-4" />
              {t('myTrip.transport.return') || 'Volta'}
              <span className="text-xs font-normal text-slate-500 ml-auto">
                {t('members.clickToFilter') || 'Clique para filtrar'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.return).length > 0 ? (
                Object.entries(stats.return).map(([type, count]) => (
                  <FilterableBadge 
                    key={`ret-${type}`} 
                    type={type} 
                    count={count} 
                    direction="return"
                    isActive={activeFilter?.type === type && activeFilter?.direction === 'return'}
                    onFilterClick={handleFilterClick}
                    language={language}
                  />
                ))
              ) : (
                <p className="text-sm text-slate-500">-</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Filter Indicator */}
      {activeFilter && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-100 dark:bg-slate-800">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-sm text-slate-600 dark:text-slate-400">
            {t('members.filteringBy') || 'Filtrando por'}:{' '}
            <span className="font-medium">
              {TRANSPORT_CONFIG[activeFilter.type]?.label[language]} ({activeFilter.direction === 'outbound' ? 'Ida' : 'Volta'})
            </span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilter}
            className="ml-auto h-7 px-2"
          >
            <X className="w-4 h-4 mr-1" />
            {t('common.clear') || 'Limpar'}
          </Button>
        </div>
      )}

      {/* Members List with Transport */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500" />
            {activeFilter 
              ? `${filteredMembers.length} ${filteredMembers.length === 1 ? 'membro' : 'membros'}`
              : (t('members.transportByMember') || 'Transporte por Membro')
            }
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredMembers.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('members.noMembersMatch') || 'Nenhum membro corresponde ao filtro'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMembers.map((member) => (
                <MemberTransportCard 
                  key={member.user_id} 
                  member={member} 
                  language={language}
                  activeFilter={activeFilter}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Separate component for member card (for cleaner code)
const MemberTransportCard = ({ member, language, activeFilter }) => {
  const outbound = member.outbound_transport_type || member.transport_type;
  const returnType = member.return_transport_type || member.transport_type;
  const outboundConfig = outbound ? TRANSPORT_CONFIG[outbound] : null;
  const returnConfig = returnType ? TRANSPORT_CONFIG[returnType] : null;
  const OutboundIcon = outboundConfig?.icon || CircleDot;
  const ReturnIcon = returnConfig?.icon || CircleDot;

  // Get transport details
  const outboundDetails = member.outbound_plane_details || member.outbound_bus_details || 
                          member.outbound_car_details || member.plane_details || 
                          member.bus_details || member.car_details;
  const returnDetails = member.return_plane_details || member.return_bus_details || 
                        member.return_car_details || member.plane_details || 
                        member.bus_details || member.car_details;

  // Check for available seats
  const outboundSeats = outbound === 'car' ? (member.outbound_car_details?.available_seats || member.car_details?.available_seats) : null;
  const returnSeats = returnType === 'car' ? (member.return_car_details?.available_seats || member.car_details?.available_seats) : null;

  const isHighlighted = activeFilter && (
    (activeFilter.direction === 'outbound' && outbound === activeFilter.type) ||
    (activeFilter.direction === 'return' && returnType === activeFilter.type)
  );

  return (
    <div 
      className={`
        flex items-center gap-3 p-3 rounded-lg transition-all
        ${isHighlighted 
          ? 'bg-orange-50 dark:bg-orange-900/20 ring-1 ring-orange-200 dark:ring-orange-800' 
          : 'bg-slate-50 dark:bg-slate-800/50'
        }
      `}
    >
      <Avatar className="w-10 h-10 flex-shrink-0">
        <AvatarFallback className="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-lg">
          {member.user_avatar || '😊'}
        </AvatarFallback>
      </Avatar>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm text-slate-900 dark:text-white truncate">
            {member.user_name}
          </p>
          {/* Show seat badge for car users */}
          {((outbound === 'car' && outboundSeats > 0) || (returnType === 'car' && returnSeats > 0)) && (
            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 text-[10px]">
              <UserPlus className="w-3 h-3 mr-0.5" />
              {outboundSeats || returnSeats} vagas
            </Badge>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1 text-xs">
          {/* Outbound */}
          <div className={`flex items-center gap-1 ${activeFilter?.direction === 'outbound' ? 'font-medium' : ''}`}>
            <ArrowRight className="w-3 h-3 text-orange-500" />
            {outbound ? (
              <div className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                <OutboundIcon className="w-3 h-3" />
                <span>{outboundConfig?.label[language] || outbound}</span>
                {outboundDetails?.departure_datetime && (
                  <span className="text-slate-400 ml-1">
                    <Clock className="w-3 h-3 inline mr-0.5" />
                    {new Date(outboundDetails.departure_datetime).toLocaleDateString(language)}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-slate-400">-</span>
            )}
          </div>
          
          {/* Return */}
          <div className={`flex items-center gap-1 ${activeFilter?.direction === 'return' ? 'font-medium' : ''}`}>
            <ArrowLeft className="w-3 h-3 text-blue-500" />
            {returnType ? (
              <div className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                <ReturnIcon className="w-3 h-3" />
                <span>{returnConfig?.label[language] || returnType}</span>
                {returnDetails?.departure_datetime && (
                  <span className="text-slate-400 ml-1">
                    <Clock className="w-3 h-3 inline mr-0.5" />
                    {new Date(returnDetails.departure_datetime).toLocaleDateString(language)}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-slate-400">-</span>
            )}
          </div>
        </div>
      </div>

      {/* Status indicators */}
      <div className="flex items-center gap-1">
        {member.is_completed ? (
          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 text-[10px]">
            ✓
          </Badge>
        ) : outbound || returnType ? (
          <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 text-[10px]">
            ...
          </Badge>
        ) : (
          <Badge className="bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 text-[10px]">
            ?
          </Badge>
        )}
      </div>
    </div>
  );
};

export default TransportSummary;
